"use client";

import { SLContainer } from "@/components/layout/SLContainer";
import { responsivePadding } from "@/lib/consts";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { important } from "@nvon/react-toolbox";
import { ReactNode } from "react";
import { SLProgressiveCarousel } from "../other/SLProgressiveCarousel";
import { HowManyCards } from "../other/slCarouselResponsivePresets";

/**
 * Props for the SLSectionCardsSlider component
 * @template T - The type of items to display in the slider/grid
 */
interface SLSectionCardsSliderProps<T> {
  /** The main title displayed at the top of the section */
  title: string;
  /** Optional subtitle displayed below the title */
  subtitle?: string;
  /** Array of items to display in cards */
  items: T[];
  /** Function that renders a card for each item */
  renderCard: (item: T) => ReactNode;
  /** Function that returns a unique key for each item (used for React keys) */
  getItemKey: (item: T) => string;
  /** Whether to display as a slider (true) or grid (false). Defaults to true */
  isSlider?: boolean;
  /** Container width. Defaults to "lg" */
  width?: "sm" | "md" | "lg" | "xl";
  /** Whether to add padding at the top. Defaults to true */
  hasPaddingTop?: boolean;
  /** Number of cards to show in slider mode. Defaults to OneToFive */
  howManyCards?: HowManyCards;
  /** Optional attribution/licensing information to display at the bottom */
  attributions?: {
    /** Items to check for attributions (can be same as items or a subset) */
    items: T[];
    /** Function that returns attribution text for an item, or null if none */
    getAttribution: (item: T) => string | null;
    /** Label to display before the attributions (e.g., "Bildnachweise") */
    attributionLabel: string;
  };
}

/**
 * A generic, reusable component for displaying cards in either a slider or grid layout.
 *
 * This component provides a flexible way to display any type of data as cards,
 * with support for both carousel/slider and grid layouts. It's designed to be
 * type-safe and reusable across different content types.
 *
 * @template T - The type of items being displayed
 *
 * @example
 * // Display courts in a slider
 * <SLSectionCardsSlider
 *   title="Zuständige Gerichte"
 *   subtitle="für das Arbeitsrecht"
 *   items={courts}
 *   renderCard={(court) => <SLCardCourt court={court} />}
 *   getItemKey={(court) => court.name}
 *   isSlider={true}
 *   howManyCards={HowManyCards.OneToFive}
 *   attributions={{
 *     items: courts,
 *     getAttribution: (court) => court.imageLicence.author ? `${court.name}: ...` : null,
 *     attributionLabel: "Bildnachweise"
 *   }}
 * />
 *
 * @example
 * // Display notaries in a grid
 * <SLSectionCardsSlider
 *   title="Notare in München"
 *   items={notaries}
 *   renderCard={(notary) => <SLCardNotary notary={notary} />}
 *   getItemKey={(notary) => notary.name}
 *   isSlider={false}
 * />
 *
 * @param props - The component props
 * @returns JSX element or null if no items provided
 */
export const SLSectionCardsSlider = <T,>({
  title,
  subtitle,
  items,
  renderCard,
  getItemKey,
  isSlider = true,
  width = "lg",
  hasPaddingTop = true,
  howManyCards = HowManyCards.OneToFive,
  attributions,
}: SLSectionCardsSliderProps<T>) => {
  // Return null if no items to display
  if (!items || items.length === 0) return null;

  const grid = (
    <Grid container spacing={4}>
      {items.map((item) => (
        <Grid
          key={getItemKey(item)}
          size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 4 }}
        >
          {renderCard(item)}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <SLContainer
      title={title}
      subtitle={subtitle}
      width={width}
      hasPaddingTop={hasPaddingTop}
    >
      {isSlider ? (
        <SLProgressiveCarousel
          howManyCards={howManyCards}
          carouselItems={items.map((item, index) => (
            <Box
              key={getItemKey(item)}
              sx={{
                px: responsivePadding,
                pl: index === 0 ? important(0) : responsivePadding,
              }}
            >
              {renderCard(item)}
            </Box>
          ))}
          grid={grid}
        />
      ) : (
        grid
      )}

      {attributions && (
        <>
          {attributions.items.some((item) =>
            attributions.getAttribution(item),
          ) && (
            <Accordion
              sx={{ backgroundColor: "transparent", mt: 6, boxShadow: "none" }}
            >
              <AccordionSummary>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    textAlign: "right",
                    width: "100%",
                  }}
                >
                  {attributions.attributionLabel}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="textSecondary">
                  {attributions.items
                    .map((item) => attributions.getAttribution(item))
                    .filter((attribution) => attribution !== null)
                    .join("; ")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </SLContainer>
  );
};
